// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/SEOSection.js

import React, { useState, useEffect } from 'react';
import { FiSave } from 'react-icons/fi';

const SEOSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const seo = websiteData?.seo || {};

  // Local states to handle typing transactions safely in local memory
  const [localMetaTitle, setLocalMetaTitle] = useState(seo.metaTitle || '');
  const [localMetaDescription, setLocalMetaDescription] = useState(seo.metaDescription || '');
  const [localKeywords, setLocalKeywords] = useState(seo.keywords || '');
  const [localCanonicalUrl, setLocalCanonicalUrl] = useState(seo.canonicalUrl || '');

  const [saving, setSaving] = useState(false);

  // Sync back from parent objects only on initial load or change of tenant configuration
  useEffect(() => {
    console.log('[SEOSection] Initializing decoupled inputs from core data baseline properties.');
    setLocalMetaTitle(seo.metaTitle || '');
    setLocalMetaDescription(seo.metaDescription || '');
    setLocalKeywords(seo.keywords || '');
    setLocalCanonicalUrl(seo.canonicalUrl || '');
  }, [seo.metaTitle, seo.metaDescription, seo.keywords, seo.canonicalUrl, tenantId]);

  const handleLocalFieldChange = (field, value) => {
    console.log(`[SEOSection] Local input updated: Field [${field}] -> Value [${value}]`);
    if (field === 'metaTitle') setLocalMetaTitle(value);
    if (field === 'metaDescription') setLocalMetaDescription(value);
    if (field === 'keywords') setLocalKeywords(value);
    if (field === 'canonicalUrl') setLocalCanonicalUrl(value);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[SEOSection] Committing isolated parameters to parent and dispatching final API put request.');
    
    try {
      // Synchronously push values back to parent states layout map
      await handleInputChange('seo', 'metaTitle', localMetaTitle);
      await handleInputChange('seo', 'metaDescription', localMetaDescription);
      await handleInputChange('seo', 'keywords', localKeywords);
      await handleInputChange('seo', 'canonicalUrl', localCanonicalUrl);

      const combinedPayload = {
        metaTitle: localMetaTitle,
        metaDescription: localMetaDescription,
        keywords: localKeywords,
        canonicalUrl: localCanonicalUrl
      };

      await handleSave('seo', combinedPayload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Row Action Bar */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">SEO Settings</h2>
            <p className="text-sm text-slate-500 mt-0.5">Optimize metadata details for online search engine algorithms</p>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={handleSaveClick}
              disabled={saving}
              type="button"
              className={`w-full sm:w-auto flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Core Settings Configuration Input Cards */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Search Attributes</h3>
          
          <div className="grid grid-cols-1 gap-4">
            
            {/* Meta Title */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Meta Title Line</label>
              <input
                type="text"
                value={localMetaTitle}
                onChange={(e) => handleLocalFieldChange('metaTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Page title for search engines"
              />
              <div className="flex items-center justify-between mt-1 px-1">
                <p className="text-[10px] text-slate-400">Recommended character span count limit layout: 50-60 characters</p>
                <span className={`text-[10px] font-bold ${localMetaTitle.length >= 50 && localMetaTitle.length <= 60 ? 'text-emerald-600' : 'text-slate-400'}`}>
                  Current: {localMetaTitle.length} ch
                </span>
              </div>
            </div>

            {/* Meta Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Meta Description block</label>
              <textarea
                value={localMetaDescription}
                onChange={(e) => handleLocalFieldChange('metaDescription', e.target.value)}
                rows="3"
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none resize-y"
                placeholder="Brief description for search results"
              />
              <div className="flex items-center justify-between mt-1 px-1">
                <p className="text-[10px] text-slate-400">Recommended character span count limit layout: 150-160 characters</p>
                <span className={`text-[10px] font-bold ${localMetaDescription.length >= 150 && localMetaDescription.length <= 160 ? 'text-emerald-600' : 'text-slate-400'}`}>
                  Current: {localMetaDescription.length} ch
                </span>
              </div>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Keywords</label>
              <input
                type="text"
                value={localKeywords}
                onChange={(e) => handleLocalFieldChange('keywords', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="education, university, college, programs, courses"
              />
              <p className="text-[10px] text-slate-400 mt-1 px-1">Comma-separated keywords relevant to your institution</p>
            </div>

            {/* Canonical URL */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Canonical URL</label>
              <input
                type="text"
                value={localCanonicalUrl}
                onChange={(e) => handleLocalFieldChange('canonicalUrl', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="https://your-institution.com"
              />
              <p className="text-[10px] text-slate-400 mt-1 px-1">The primary URL for your website to avoid duplicate content issues</p>
            </div>

          </div>
        </div>

        {/* Realtime Live Search Results Engine Result Card Preview */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-inner">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Live Search Result Preview</h3>
          <div className="bg-slate-50 border border-slate-150 rounded-xl p-4 max-w-xl">
            <div className="space-y-1 overflow-hidden">
              <div className="text-blue-800 text-lg font-medium hover:underline cursor-pointer truncate tracking-normal">
                {localMetaTitle || 'Your Custom Meta Title Content String'}
              </div>
              <div className="text-emerald-700 text-xs truncate font-medium">
                {localCanonicalUrl || 'https://your-institution.com'}
              </div>
              <div className="text-slate-600 text-xs leading-relaxed break-words">
                {localMetaDescription || 'Your contextual metadata page descriptions details snippet block data summary text item row goes here...'}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SEOSection;